import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Plus, Building2, Phone, Mail, MapPin, FileText, Trash2, Edit, Calendar, Star } from 'lucide-react';
import { toast } from 'sonner';
import { STATUT_COLORS, STATUT_LABELS } from '@/lib/statutsConfig';
import { ModifierClient } from '@/components/crm/ModifierClient';
import { RecapBTInternesAnnuel } from '@/components/crm/RecapBTInternesAnnuel';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GoogleMapsBtn } from '@/components/GoogleMapsBtn';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { usePermissions } from '@/lib/PermissionsContext';
import { useAuth } from '@/lib/AuthContext';
import { formatLocalDate } from '@/utils/formatLocalTime'; // 🆕 [P1] timezone Québec centralisé

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

async function sendAvisGoogle(clientId, clientNom) {
  try {
    const r = await axios.post(`${API_URL}/api/sms/avis-google/${clientId}`);
    const d = r.data;
    if (r.status === 200) {
      alert(`✅ Demande d'avis Google envoyée à ${clientNom} !`);
    } else {
      alert(`Erreur: ${d.detail || "Numéro de téléphone manquant"}`);
    }
  } catch {
    alert("Erreur lors de l'envoi");
  }
}

export function FicheClient() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canDoBonTravailAction } = usePermissions();
  const { isProprietaire } = useAuth();
  const [client, setClient] = useState(null);
  const [projets, setProjets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModifier, setShowModifier] = useState(false);
  const [deleteProjet, setDeleteProjet] = useState(null);
  const [editProjet, setEditProjet] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [employees, setEmployees] = useState([]);
  // 🆕 [Phase 30 #4] Toggle client interne (owner only)
  const [toggleInternalBusy, setToggleInternalBusy] = useState(false);

  useEffect(() => {
    fetchClient();
    fetchProjets();
  }, [id]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/crm/clients/${id}`);
      if (response.status !== 200) throw new Error('Client non trouvé');
      const data = response.data;
      setClient(data);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Client non trouvé');
      navigate('/crm/clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/crm/dossiers?client_id=${id}`);
      if (response.status !== 200) throw new Error('Erreur');
      const data = response.data;
      setProjets(data.dossiers || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleDeleteProjet = async () => {
    if (!deleteProjet) return;
    try {
      const res = await axios.delete(`${API_URL}/api/crm/dossiers/${deleteProjet.id}`);
      if (res.status !== 200) throw new Error('Erreur');
      toast.success(`Projet "${deleteProjet.nom_dossier}" supprimé`);
      setDeleteProjet(null);
      fetchProjets();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees`);
      if (res.status === 200) { const data = res.data; setEmployees(data.employees || []); }
    } catch (e) { /* ignore */ }
  }, []);

  const openEditProjet = (projet) => {
    setEditForm({
      nom_dossier: projet.nom_dossier || '',
      departement: projet.departement || 'PRQC',
      type_contrat: projet.type_contrat || 'peinture',
      adresse_travaux: projet.adresse_travaux || '',
      ville_travaux: projet.ville_travaux || '',
      code_postal_travaux: projet.code_postal_travaux || '',
      representant_id: projet.representant_id || '',
      representant_nom: projet.representant_nom || '',
      chef_equipe_id: projet.chef_equipe_id || '',
      chef_equipe_nom: projet.chef_equipe_nom || '',
      charge_projet_id: projet.charge_projet_id || '',
      charge_projet_nom: projet.charge_projet_nom || '',
      date_production: projet.date_production || '',
    });
    setEditProjet(projet);
    fetchEmployees();
  };

  const handleAssign = (field, empId) => {
    if (empId === 'aucun') {
      setEditForm(prev => ({ ...prev, [`${field}_id`]: '', [`${field}_nom`]: '' }));
    } else {
      const emp = employees.find(e => e.id === empId);
      if (emp) setEditForm(prev => ({ ...prev, [`${field}_id`]: empId, [`${field}_nom`]: `${emp.prenom} ${emp.nom}` }));
    }
  };

  const handleSaveProjet = async () => {
    try {
      const payload = { ...editProjet, ...editForm };
      const res = await axios.put(`${API_URL}/api/crm/dossiers/${editProjet.id}`, payload);
      if (res.status !== 200) throw new Error('Erreur');
      toast.success('Projet modifié!');
      setEditProjet(null);
      fetchProjets();
    } catch (e) { toast.error('Erreur lors de la modification'); }
  };

  // 🆕 [Phase 30 #4] Toggle client interne (owner only) — PATCH /toggle-internal-flag
  const handleToggleInternalClient = async (checked) => {
    if (!client || toggleInternalBusy) return;
    setToggleInternalBusy(true);
    try {
      const res = await axios.patch(
        `${API_URL}/api/crm/clients/${client.id}/toggle-internal-flag`,
        { is_internal_client: !!checked },
      );
      setClient((c) => ({ ...c, is_internal_client: !!res.data?.is_internal_client }));
      toast.success(checked
        ? '✅ Client marqué comme « interne » — exclu des vues commerciales.'
        : 'Client retiré du flag « interne ».');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du toggle.');
    } finally {
      setToggleInternalBusy(false);
    }
  };

  if (loading || !client) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/crm/clients')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux clients
        </Button>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{client.nom}</h1>
            <Badge variant="outline" className={client.type_client === 'commercial' ? 'border-purple-600 text-purple-600' : ''}>
              {client.type_client === 'commercial' ? 'Commercial' : 'Résidentiel'}
            </Badge>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700 self-start sm:self-auto" onClick={() => setShowModifier(true)}>Modifier le client</Button>
        </div>

        {/* 🆕 [Phase 30 #4] Checkbox client interne (owner only) */}
        {isProprietaire && (
          <label
            className={
              'mt-3 flex items-center gap-2 text-sm bg-purple-50 border-2 border-purple-200 px-3 py-2 rounded-lg w-fit cursor-pointer hover:bg-purple-100 transition '
              + (toggleInternalBusy ? 'opacity-60 cursor-wait' : '')
            }
            data-testid="client-internal-toggle-label"
          >
            <input
              type="checkbox"
              checked={!!client.is_internal_client}
              onChange={(e) => handleToggleInternalClient(e.target.checked)}
              disabled={toggleInternalBusy}
              className="w-4 h-4 accent-purple-600"
              data-testid="client-internal-toggle"
            />
            <Building2 className="w-4 h-4 text-purple-600" />
            <span className="font-medium text-purple-900">
              Ce client est un client interne (entreprise)
            </span>
            <span className="text-xs text-purple-600">
              — exclu des vues commerciales (CA, marges, fournisseurs)
            </span>
          </label>
        )}
      </div>

      {/* 🆕 [Phase 30 #4] Récap BT internes — affiché seulement si client interne */}
      {client.is_internal_client && (
        <div className="mb-6" data-testid="recap-bt-internes-wrapper">
          <RecapBTInternesAnnuel clientId={client.id} />
        </div>
      )}

      {/* Informations Client */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-400" />
              <span className="text-sm">{client.telephone}</span>
            </div>
            {client.email && (
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-sm">{client.email}</span>
              </div>
            )}
            {client.contact_nom && (
              <div className="pt-2 border-t text-sm">
                <p className="text-gray-500 text-xs">Contact commercial</p>
                <p className="font-medium">{client.contact_nom}</p>
                {client.contact_telephone && <p className="text-gray-600">{client.contact_telephone}</p>}
              </div>
            )}
            {client.telephone && (
              <button
                onClick={() => sendAvisGoogle(client.id, `${client.prenom || ''} ${client.nom || client.name || ''}`.trim())}
                data-testid="avis-google-btn"
                className="mt-2 flex items-center gap-1.5 text-xs bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border border-yellow-200 px-3 py-1.5 rounded-lg transition-colors font-medium w-full justify-center"
              >
                <Star className="w-3.5 h-3.5 fill-yellow-400 stroke-yellow-500" />
                Demander Avis Google ⭐
              </button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Adresse</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-2 flex-wrap">
              <MapPin className="w-4 h-4 text-gray-400 mt-1 shrink-0" />
              <div className="text-sm">
                <p>{client.adresse_client}</p>
                <p className="flex items-center gap-2">{client.ville_client}, {client.code_postal_client}
                  <GoogleMapsBtn address={client.adresse_client} ville={client.ville_client} codePostal={client.code_postal_client} />
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Statistiques</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Nombre de projets</p>
                <p className="text-2xl font-bold text-blue-600">{projets.length}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Client depuis</p>
                <p className="text-sm font-semibold">{formatLocalDate(client.date_creation)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projets du Client */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Projets de ce Client ({projets.length})</CardTitle>
            <Button onClick={() => navigate(`/crm/creer-projet?client_id=${client.id}`)} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" /> Nouveau Projet
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {projets.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Aucun projet pour ce client</p>
              <Button onClick={() => navigate(`/crm/creer-projet?client_id=${client.id}`)}>
                <Plus className="w-4 h-4 mr-2" /> Créer le premier projet
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {projets.map((projet) => (
                <Card key={projet.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex flex-col gap-2">
                      <div className="flex justify-between items-start gap-2">
                        <Link to={`/crm/projets/${projet.id}`} className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <p className="font-bold text-base leading-tight">{projet.nom_dossier}</p>
                            {/* 🆕 [Phase 33] Masquer le badge de statut CRM commercial sur les projets d'un client interne */}
                            {!client?.is_internal_client && (
                              <Badge className={STATUT_COLORS[projet.statut] || 'bg-gray-100'} style={{fontSize:'0.65rem'}}>
                                {STATUT_LABELS[projet.statut] || projet.statut}
                              </Badge>
                            )}
                            <Badge variant="outline" style={{fontSize:'0.65rem'}}>{projet.departement}</Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{projet.numero_dossier}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                            <div>
                              <p className="text-gray-500">Adresse travaux</p>
                              <p className="font-medium">{projet.ville_travaux}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Représentant</p>
                              <p className="font-medium">{projet.representant_nom || 'Non assigné'}</p>
                            </div>
                            {projet.montant_soumission > 0 && canDoBonTravailAction('bon_voir_montant') && (
                              <div>
                                <p className="text-gray-500">Montant</p>
                                <p className="font-bold text-green-600">
                                  {projet.montant_soumission.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}
                                </p>
                              </div>
                            )}
                          </div>
                        </Link>
                        {/* Actions desktop uniquement */}
                        <div className="hidden sm:flex gap-2 flex-shrink-0">
                          <Button size="sm" variant="outline" onClick={() => navigate(`/crm/calendrier-rendez-vous?dossier_id=${projet.id}&client_id=${client.id}`)}>
                            <Calendar className="w-4 h-4 mr-1" /> Céduler
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEditProjet(projet)}>
                            <Edit className="w-4 h-4 mr-1" /> Modifier
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setDeleteProjet(projet)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      {/* Actions mobile — pleine largeur */}
                      <div className="flex gap-2 sm:hidden pt-2 border-t">
                        <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => navigate(`/crm/projets/${projet.id}`)}>
                          <Edit className="w-3.5 h-3.5 mr-1" /> Ouvrir
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => openEditProjet(projet)}>
                          <Edit className="w-3.5 h-3.5 mr-1" /> Modifier
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setDeleteProjet(projet)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ModifierClient
        isOpen={showModifier}
        onClose={() => setShowModifier(false)}
        client={client}
        onSuccess={() => { fetchClient(); setShowModifier(false); }}
      />

      {/* Dialog suppression projet */}
      <Dialog open={!!deleteProjet} onOpenChange={() => setDeleteProjet(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer un projet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">Êtes-vous sûr de vouloir supprimer le projet <strong>"{deleteProjet?.nom_dossier}"</strong> ({deleteProjet?.numero_dossier}) ?</p>
            <p className="text-sm text-red-600 font-medium">Cette action est irréversible.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDeleteProjet(null)}>Annuler</Button>
              <Button variant="destructive" onClick={handleDeleteProjet}><Trash2 className="w-4 h-4 mr-2" /> Oui, supprimer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog modifier projet */}
      <Dialog open={!!editProjet} onOpenChange={() => setEditProjet(null)}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[85vh] overflow-y-auto !top-[5%] !translate-y-0 mx-auto" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Modifier le Projet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom du projet *</Label>
              <Input value={editForm.nom_dossier || ''} onChange={(e) => setEditForm({ ...editForm, nom_dossier: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Département</Label>
                <Select value={editForm.departement || 'PRQC'} onValueChange={(val) => setEditForm({ ...editForm, departement: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRQC">PRQC</SelectItem>
                    <SelectItem value="LMDS">LMDS</SelectItem>
                    <SelectItem value="LOCATION">LOCATION</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type de contrat</Label>
                <Select value={editForm.type_contrat || 'peinture'} onValueChange={(val) => setEditForm({ ...editForm, type_contrat: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="peinture">Peinture</SelectItem>
                    <SelectItem value="location">Location</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Adresse des travaux</h3>
              <div className="space-y-3">
                <Input value={editForm.adresse_travaux || ''} onChange={(e) => setEditForm({ ...editForm, adresse_travaux: e.target.value })} placeholder="Adresse" />
                <div className="grid grid-cols-2 gap-4">
                  <Input value={editForm.ville_travaux || ''} onChange={(e) => setEditForm({ ...editForm, ville_travaux: e.target.value })} placeholder="Ville" />
                  <Input value={editForm.code_postal_travaux || ''} onChange={(e) => setEditForm({ ...editForm, code_postal_travaux: e.target.value })} placeholder="Code postal" />
                </div>
              </div>
            </div>
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Assignations</h3>
              <div className="space-y-4">
                <div>
                  <Label>Représentant</Label>
                  <Select value={editForm.representant_id || 'aucun'} onValueChange={(v) => handleAssign('representant', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aucun">Aucun</SelectItem>
                      {employees.filter(e => e.type_employe === 'representant').map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                      ))}
                      {employees.filter(e => e.type_employe === 'representant').length === 0 && employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Chef d'équipe</Label>
                  <Select value={editForm.chef_equipe_id || 'aucun'} onValueChange={(v) => handleAssign('chef_equipe', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aucun">Aucun</SelectItem>
                      {employees.filter(e => e.type_employe === 'terrain' && e.role_terrain === 'chef_equipe').map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                      ))}
                      {employees.filter(e => e.type_employe === 'terrain' && e.role_terrain === 'chef_equipe').length === 0 &&
                        employees.filter(e => e.type_employe === 'terrain').map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Chargé de projet</Label>
                  <Select value={editForm.charge_projet_id || 'aucun'} onValueChange={(v) => handleAssign('charge_projet', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aucun">Aucun</SelectItem>
                      {employees.filter(e => e.type_employe === 'admin' && e.role_admin === 'charge_projet').map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                      ))}
                      {employees.filter(e => e.type_employe === 'admin' && e.role_admin === 'charge_projet').length === 0 &&
                        employees.filter(e => e.type_employe === 'admin').map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date de production prévue</Label>
                  <Input type="date" value={editForm.date_production || ''} onChange={(e) => setEditForm({ ...editForm, date_production: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditProjet(null)}>Annuler</Button>
              <Button onClick={handleSaveProjet} className="bg-blue-600 hover:bg-blue-700">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
